import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormHelperText,
  InputAdornment,
  Link,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import React, { useEffect, useRef, useState } from "react";
import ReCAPTCHA from "react-google-recaptcha";

import cinchLogo from "../../assets/cinch-logo.svg";
import { getSignupStatus, submitSignup } from "../../api";
import useRecaptcha from "../../hooks/use-recaptcha";
import posthog, { getUtmPayload, isInternalEmail } from "../../posthog";
import { RECAPTCHA_SITE_KEY } from "../../recaptcha-key";
import { NAICS_OPTIONS, NAICS_OTHER } from "./naics-options";
import SignupSuccessScreen from "./signup-success-screen";

const FEATURE_FOCUS = "scheduler";

const INITIAL_FORM = {
  first_name: "",
  last_name: "",
  email: "",
  company_name: "",
  naics_choice: "",
  naics_other: "",
  terms_accepted: false,
};

const TERMS_URL = "https://cinch.io/terms/";

const REQUIRED_FIELDS = ["first_name", "last_name", "email", "company_name"];

const ROUNDED_INPUT_SX = {
  "& .MuiOutlinedInput-root": { borderRadius: 1.5 },
  "& .MuiInputLabel-asterisk": { display: "none" },
};

const REQUIRED_ADORNMENT = (
  <InputAdornment position="end">
    <Box
      component="span"
      sx={{ color: "#D32F2F", fontSize: "1rem", lineHeight: 1 }}
    >
      *
    </Box>
  </InputAdornment>
);

// Solid Cinch blue rectangle matching Figma V2 — full-width, light
// rounded corners, no gradient.
const SUBMIT_BUTTON_SX = {
  py: 1.25,
  borderRadius: 1.5,
  textTransform: "none",
  fontWeight: 600,
  fontSize: "1rem",
  color: "#fff",
  bgcolor: "#0994F2",
  boxShadow: "none",
  "&:hover": { bgcolor: "#077BCE", boxShadow: "none" },
  "&.Mui-disabled": { bgcolor: "#0994F2", opacity: 0.6, color: "#fff" },
};

export default function SchedulerSignup() {
  const [form, setForm] = useState(INITIAL_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [captchaOpen, setCaptchaOpen] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});
  // null = unknown / still fetching. We default to enabled if the health
  // check fails — better to let the user try and get a 503 than to block
  // them from a working signup because of a transient network blip.
  const [signupEnabled, setSignupEnabled] = useState(null);
  const [signupDisabledMessage, setSignupDisabledMessage] = useState(null);

  const formStartedRef = useRef(false);

  useEffect(() => {
    posthog.capture("signup_page_viewed", { feature_focus: FEATURE_FOCUS });
    const previousTitle = document.title;
    document.title = "Sign Up — Cinch Scheduler";
    return () => {
      document.title = previousTitle;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    getSignupStatus(FEATURE_FOCUS)
      .then((response) => {
        if (cancelled) return;
        setSignupEnabled(Boolean(response.data?.signup_enabled));
        setSignupDisabledMessage(response.data?.message || null);
      })
      .catch(() => {
        if (cancelled) return;
        // Open by default on health-check failure — see comment above.
        setSignupEnabled(true);
        setSignupDisabledMessage(null);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const { recaptchaRef, handleRecaptcha, setCaptchaToken } = useRecaptcha();

  const theme = useTheme();
  const fullScreenCaptcha = useMediaQuery(theme.breakpoints.down("sm"));

  if (result) {
    const handleStartOver = () => {
      setResult(null);
      setCaptchaToken(null);
      setCaptchaOpen(false);
      setError(null);
      setFieldErrors({});
      if (recaptchaRef.current) {
        recaptchaRef.current.reset();
      }
    };
    return (
      <SignupSuccessScreen
        email={form.email.trim()}
        onStartOver={handleStartOver}
        featureFocus={FEATURE_FOCUS}
      />
    );
  }

  const update = (field) => (e) => {
    if (!formStartedRef.current) {
      formStartedRef.current = true;
      posthog.capture("signup_form_started", { feature_focus: FEATURE_FOCUS });
    }
    const value =
      e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setForm((prev) => ({ ...prev, [field]: value }));
    setFieldErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  const naicsIsOther = form.naics_choice === NAICS_OTHER;

  // Cheap client-side check before showing the captcha modal — keeps
  // users from solving a captcha just to get a 400 back. The backend
  // is still the source of truth for validation.
  const validateClientSide = () => {
    const errs = {};
    REQUIRED_FIELDS.forEach((f) => {
      if (!form[f].trim()) errs[f] = ["This field is required."];
    });
    if (!form.naics_choice) {
      errs.naics = ["Please pick an industry."];
    } else if (naicsIsOther && !form.naics_other.trim()) {
      errs.naics = ["Please tell us about your industry."];
    }
    if (!form.terms_accepted) {
      errs.terms_accepted = [
        "You must agree to the Terms of Service to continue.",
      ];
    }
    return errs;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setError(null);
    const errs = validateClientSide();
    if (Object.keys(errs).length > 0) {
      setFieldErrors(errs);
      setError("Please correct the highlighted fields and try again.");
      posthog.capture("signup_validation_failed", {
        feature_focus: FEATURE_FOCUS,
        fields: Object.keys(errs),
      });
      return;
    }
    setFieldErrors({});
    posthog.register({ is_internal_user: isInternalEmail(form.email) });
    setCaptchaOpen(true);
    posthog.capture("signup_captcha_shown", { feature_focus: FEATURE_FOCUS });
  };

  const handleCancelCaptcha = () => {
    setCaptchaOpen(false);
    if (recaptchaRef.current) recaptchaRef.current.reset();
    setCaptchaToken("");
  };

  // Captcha is solved → fire the actual API call. We submit directly
  // from this callback rather than waiting on `captchaToken` state to
  // settle, since the next render isn't guaranteed before the user
  // (or test code) advances.
  const onCaptchaChange = async (token) => {
    handleRecaptcha(token);
    if (!token) return;

    posthog.capture("signup_captcha_solved", { feature_focus: FEATURE_FOCUS });

    const naics = naicsIsOther ? form.naics_other.trim() : form.naics_choice;
    const email = form.email.trim();
    const payload = {
      first_name: form.first_name.trim(),
      last_name: form.last_name.trim(),
      email,
      company_name: form.company_name.trim(),
      naics,
      terms_accepted: form.terms_accepted,
      ...getUtmPayload(),
      captcha: token,
    };

    setSubmitting(true);
    try {
      const response = await submitSignup(FEATURE_FOCUS, payload);
      const status = response.data?.status || "success";
      posthog.capture("signup_completed", {
        feature_focus: FEATURE_FOCUS,
        status,
      });
      // Identify on any non-error response. The BE deliberately returns
      // an identical "success" shape for both new-account creation and
      // duplicate-email submissions (so the endpoint can't be probed for
      // account enumeration), which means the frontend can no longer tell
      // them apart. For a duplicate we'll be tagging the existing user's
      // PostHog profile with this anonymous session's events; acceptable
      // tradeoff for the security win. If that ever bites analytics, the
      // fix is to move identify() into the password-set completion flow
      // (which only fires for genuinely new accounts).
      if (status === "success") {
        posthog.identify(email.toLowerCase(), {
          email,
          name: `${payload.first_name} ${payload.last_name}`.trim(),
          company_name: payload.company_name,
          signup_feature_focus: FEATURE_FOCUS,
          is_internal_user: isInternalEmail(email),
        });
      }
      setResult(response.data);
      setCaptchaOpen(false);
    } catch (err) {
      const httpStatus = err.response?.status;
      let errorKind = "unknown";
      if (httpStatus === 400 && err.response.data?.errors) {
        errorKind = "validation";
        setFieldErrors(err.response.data.errors);
        setError("Please correct the highlighted fields and try again.");
      } else if (httpStatus === 429) {
        errorKind = "rate_limited";
        setError(
          "We've received a lot of requests from your network. Please try again in a bit.",
        );
      } else if (httpStatus === 503) {
        // Kill switch — surface the BE-supplied message (e.g. "Signups are
        // temporarily unavailable…") so the user understands it's not them.
        errorKind = "kill_switch";
        setError(
          err.response.data?.message ||
            "Signups are temporarily unavailable. Please try again later or contact support.",
        );
      } else {
        errorKind = "server_error";
        setError(
          "Something went wrong on our end. Please try again in a moment.",
        );
      }
      posthog.capture("signup_failed", {
        feature_focus: FEATURE_FOCUS,
        http_status: httpStatus || null,
        error_kind: errorKind,
      });
      // Close the modal so the user sees the form-level error / can fix
      // any flagged fields. Reset the captcha so the next attempt
      // requires a fresh token.
      setCaptchaOpen(false);
      if (recaptchaRef.current) recaptchaRef.current.reset();
      setCaptchaToken("");
    } finally {
      setSubmitting(false);
    }
  };

  const handleSignInLinkClick = () => {
    posthog.capture("signup_existing_account_link_clicked", {
      feature_focus: FEATURE_FOCUS,
    });
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: { xs: "flex-start", sm: "center" },
        bgcolor: "#FFFFFF",
        p: { xs: 2, sm: 4 },
        boxSizing: "border-box",
      }}
    >
      <Box
        role="img"
        aria-label="Cinch"
        sx={{
          width: 110,
          height: 38,
          mb: 3,
          bgcolor: "#0994F2",
          mask: `url(${cinchLogo}) no-repeat center / contain`,
          WebkitMask: `url(${cinchLogo}) no-repeat center / contain`,
        }}
      />
      <Paper
        elevation={3}
        sx={{
          width: "100%",
          maxWidth: 460,
          boxSizing: "border-box",
          p: { xs: 3, sm: 4 },
          borderRadius: 3,
          boxShadow: "0 4px 20px rgba(0, 0, 0, 0.08)",
        }}
      >
        <Typography
          variant="h5"
          sx={{ fontWeight: 700, textAlign: "center", color: "#111827" }}
        >
          Set Up Your Free Booking Calendar
        </Typography>
        <Typography
          variant="body2"
          sx={{ mt: 0.75, mb: 3, textAlign: "center", color: "text.secondary" }}
        >
          Take appointments from your website in about ten minutes. Free with no
          credit card.
        </Typography>

        {signupEnabled === false && (
          <Alert severity="warning" sx={{ mb: 3 }}>
            {signupDisabledMessage ||
              "Signups are temporarily unavailable. Please try again later or contact support."}
          </Alert>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        <form onSubmit={handleSubmit}>
          <Stack spacing={1.5}>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
              <TextField
                label="First Name"
                required
                fullWidth
                value={form.first_name}
                onChange={update("first_name")}
                error={Boolean(fieldErrors.first_name)}
                helperText={fieldErrors.first_name?.[0]}
                autoComplete="given-name"
                inputProps={{ maxLength: 60 }}
                InputProps={{ endAdornment: REQUIRED_ADORNMENT }}
                sx={ROUNDED_INPUT_SX}
              />
              <TextField
                label="Last Name"
                required
                fullWidth
                value={form.last_name}
                onChange={update("last_name")}
                error={Boolean(fieldErrors.last_name)}
                helperText={fieldErrors.last_name?.[0]}
                autoComplete="family-name"
                inputProps={{ maxLength: 60 }}
                InputProps={{ endAdornment: REQUIRED_ADORNMENT }}
                sx={ROUNDED_INPUT_SX}
              />
            </Stack>

            <TextField
              label="Email"
              type="email"
              required
              fullWidth
              value={form.email}
              onChange={update("email")}
              error={Boolean(fieldErrors.email)}
              helperText={fieldErrors.email?.[0]}
              autoComplete="email"
              inputProps={{
                inputMode: "email",
                autoCapitalize: "none",
                autoCorrect: "off",
                spellCheck: false,
                maxLength: 254,
              }}
              InputProps={{ endAdornment: REQUIRED_ADORNMENT }}
              sx={ROUNDED_INPUT_SX}
            />

            <TextField
              label="Company Name"
              required
              fullWidth
              value={form.company_name}
              onChange={update("company_name")}
              error={Boolean(fieldErrors.company_name)}
              helperText={fieldErrors.company_name?.[0]}
              autoComplete="organization"
              inputProps={{ maxLength: 150 }}
              InputProps={{ endAdornment: REQUIRED_ADORNMENT }}
              sx={ROUNDED_INPUT_SX}
            />

            <TextField
              select
              label="Industry"
              required
              fullWidth
              value={form.naics_choice}
              onChange={update("naics_choice")}
              error={Boolean(fieldErrors.naics)}
              helperText={fieldErrors.naics?.[0]}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end" sx={{ mr: 2.5 }}>
                    <Box
                      component="span"
                      sx={{ color: "#D32F2F", fontSize: "1rem", lineHeight: 1 }}
                    >
                      *
                    </Box>
                  </InputAdornment>
                ),
              }}
              sx={ROUNDED_INPUT_SX}
            >
              {NAICS_OPTIONS.map((opt) => (
                <MenuItem key={opt.id} value={opt.id}>
                  {opt.title}
                </MenuItem>
              ))}
              <MenuItem value={NAICS_OTHER}>Not listed</MenuItem>
            </TextField>

            {naicsIsOther && (
              <TextField
                label="Tell us about your industry"
                required
                fullWidth
                value={form.naics_other}
                onChange={update("naics_other")}
                placeholder="e.g. Mobile dog grooming"
                inputProps={{ maxLength: 255 }}
                InputProps={{ endAdornment: REQUIRED_ADORNMENT }}
                sx={ROUNDED_INPUT_SX}
              />
            )}

            <FormControl
              required
              error={Boolean(fieldErrors.terms_accepted)}
              component="fieldset"
              sx={{ mt: 0.5 }}
            >
              <FormControlLabel
                control={
                  <Checkbox
                    checked={form.terms_accepted}
                    onChange={update("terms_accepted")}
                    size="small"
                  />
                }
                label={
                  <Typography variant="body2" sx={{ color: "text.secondary" }}>
                    I agree to the{" "}
                    <Link
                      href={TERMS_URL}
                      target="_blank"
                      rel="noopener noreferrer"
                      underline="hover"
                    >
                      Terms of Service
                    </Link>
                    .
                  </Typography>
                }
                sx={{ mr: 0 }}
              />
              {fieldErrors.terms_accepted && (
                <FormHelperText sx={{ ml: 4 }}>
                  {fieldErrors.terms_accepted[0]}
                </FormHelperText>
              )}
            </FormControl>

            <Button
              type="submit"
              variant="contained"
              size="large"
              fullWidth
              disabled={submitting || signupEnabled === false}
              sx={SUBMIT_BUTTON_SX}
            >
              Sign Up
            </Button>

            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ textAlign: "center" }}
            >
              Already have an account?{" "}
              <Link
                href="https://marketing.cinch.io/sign-in"
                underline="hover"
                onClick={handleSignInLinkClick}
              >
                Sign in
              </Link>
            </Typography>
          </Stack>
        </form>
      </Paper>

      <Dialog
        open={captchaOpen}
        onClose={submitting ? undefined : handleCancelCaptcha}
        aria-labelledby="captcha-dialog-title"
        fullScreen={fullScreenCaptcha}
        PaperProps={{
          sx: {
            borderRadius: { xs: 0, sm: 4 },
            px: { xs: 0, sm: 2 },
            py: 1,
            minWidth: { xs: "auto", sm: 380 },
          },
        }}
      >
        <DialogTitle
          id="captcha-dialog-title"
          sx={{ pb: 0.5, fontWeight: 600, px: { xs: 2, sm: 3 } }}
        >
          One quick check
        </DialogTitle>
        <DialogContent sx={{ pt: 2, px: { xs: 0.5, sm: 3 } }}>
          <DialogContentText sx={{ mb: 2.5, px: { xs: 1.5, sm: 0 } }}>
            Confirm you're human and we'll create your account.
          </DialogContentText>
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              minHeight: 90,
              alignItems: "center",
            }}
          >
            {submitting ? (
              <Stack direction="row" spacing={2} alignItems="center">
                <CircularProgress size={20} />
                <Typography variant="body2">Creating your account…</Typography>
              </Stack>
            ) : (
              <ReCAPTCHA
                ref={recaptchaRef}
                sitekey={RECAPTCHA_SITE_KEY}
                onChange={onCaptchaChange}
              />
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, pt: 1 }}>
          <Button
            onClick={handleCancelCaptcha}
            disabled={submitting}
            variant="contained"
            color="inherit"
            sx={{
              textTransform: "none",
              borderRadius: 999,
              px: 3,
              bgcolor: "#E4E8EE",
              color: "#1D252D",
              boxShadow: "none",
              "&:hover": { bgcolor: "#D5DAE2", boxShadow: "none" },
            }}
          >
            Cancel
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
